import { useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  CheckCircle2,
  Clock,
  FileText,
  Inbox,
  Search,
  XCircle,
} from "lucide-react";
import {
  filterGroupSchema,
  parseFilterFromSearchParam,
  SUBMISSION_STATUSES,
  visibleColumnKeys,
  type FilterTree,
  type SubmissionStatus,
} from "@colab/shared";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { MetricCard } from "@/components/shared/metric-card";
import {
  ActiveViewColumnsButton,
  AddFilterMenu,
  SavedViewTabs,
} from "@/components/shared/saved-views";
import { useViews } from "@/features/admin/views/use-views";
import { useActiveView } from "@/features/admin/views/active-view";
import { useAdminSubmissions } from "./use-admin-submissions";
import { useSubmissionsAggregate } from "./use-submissions-aggregate";
import { SubmissionsTable } from "./submissions-table";
import { SubmissionDrawer } from "./submission-drawer";
import type { AdminSubmissionsSortColumn } from "./types";

// Phase 7 sub-bucket 19 — admin submissions queue Saved Views consumer.
//
// Builds on sub-bucket 17's drawer-as-shareable-route + chip toolbar by adding
// the Saved Views surface (catalog stabilised in this sub-bucket via
// segments-meta swap form_id → form_name / partner_id → partner_name; the
// worker post-filters those two columns in-memory after the denormalisation
// join). The page mirrors `requests-queue-page.tsx` (mdf-requests, sub-bucket
// 17): SavedViewTabs always render, AddFilterMenu becomes the only filter
// affordance when a view OR url filter is driving the read, and the chip
// toolbar hides otherwise.
//
// URL state:
//
//   ?status=pending|accepted|declined      — chip filter (hidden when filtering)
//   ?sort=created_at|updated_at|decided_at|status
//   ?dir=asc|desc
//   ?q=<text>                              — client-side filter
//   ?filter=<base64-json>                  — ad-hoc FilterTree
//   ?view=<id>                             — active saved view (sub-bucket 16)
//   ?submission=<uuid>                     — drawer-as-shareable-route

const ENTITY_TYPE = "submission" as const;

const STATUS_OPTIONS: ReadonlyArray<{
  value: "all" | SubmissionStatus;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
];

const ALLOWED_SORT: ReadonlySet<AdminSubmissionsSortColumn> = new Set([
  "created_at",
  "updated_at",
  "decided_at",
  "status",
]);

function isSubmissionStatus(value: string): value is SubmissionStatus {
  return (SUBMISSION_STATUSES as readonly string[]).includes(value);
}

export function SubmissionsQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusRaw = searchParams.get("status");
  const status: SubmissionStatus | null =
    statusRaw && isSubmissionStatus(statusRaw) ? statusRaw : null;
  const sortRaw = searchParams.get("sort");
  const sortColumn: AdminSubmissionsSortColumn =
    sortRaw && ALLOWED_SORT.has(sortRaw as AdminSubmissionsSortColumn)
      ? (sortRaw as AdminSubmissionsSortColumn)
      : "created_at";
  const sortDir: "asc" | "desc" =
    searchParams.get("dir") === "asc" ? "asc" : "desc";
  const q = searchParams.get("q") ?? "";
  const selectedId = searchParams.get("submission");

  // ---- Saved Views resolution ----
  const viewsQuery = useViews(ENTITY_TYPE);
  const active = useActiveView(viewsQuery.data);
  const urlFilter = useMemo<FilterTree | null>(() => {
    const raw = searchParams.get("filter");
    if (!raw) return null;
    let decoded: string | null = null;
    try {
      decoded = atob(raw);
    } catch {
      decoded = raw;
    }
    const parsed = parseFilterFromSearchParam(decoded);
    if (parsed === null) return null;
    const validated = filterGroupSchema.safeParse(parsed);
    return validated.success ? validated.data : null;
  }, [searchParams]);

  const effectiveFilter = urlFilter ?? active.filter;
  const filterIsOverride = urlFilter !== null && active.filter !== null;
  const filterDriving = effectiveFilter !== null;

  const activeVisible = useMemo(
    () => visibleColumnKeys(active.columns, ENTITY_TYPE),
    [active.columns],
  );

  function updateParam(key: string, value: string | null) {
    const sp = new URLSearchParams(searchParams);
    if (value === null || value === "" || value === "all") {
      sp.delete(key);
    } else {
      sp.set(key, value);
    }
    setSearchParams(sp, { replace: true });
  }

  function setFilterInUrl(tree: FilterTree | null) {
    const sp = new URLSearchParams(searchParams);
    if (tree) {
      sp.set("filter", btoa(JSON.stringify(tree)));
    } else {
      sp.delete("filter");
    }
    setSearchParams(sp, { replace: true });
  }

  const submissionsQuery = useAdminSubmissions({
    // Chip filter short-circuits when a FilterTree drives the read.
    status: filterDriving ? null : status,
    sortColumn,
    sortDir,
    filterNode: effectiveFilter,
  });
  const aggregateQuery = useSubmissionsAggregate();

  const filteredRows = useMemo(() => {
    const rows = submissionsQuery.data ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.form_name.toLowerCase().includes(term) ||
        (r.partner_name ?? "").toLowerCase().includes(term),
    );
  }, [submissionsQuery.data, q]);

  const totals = aggregateQuery.data ?? {
    total: 0,
    pending: 0,
    accepted: 0,
    declined: 0,
  };

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Submissions
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Inbound submissions from forms — review, accept, or decline.
          </p>
        </div>
      </header>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total" value={String(totals.total)} icon={Inbox} />
        <MetricCard
          label="Pending"
          value={String(totals.pending)}
          icon={Clock}
        />
        <MetricCard
          label="Accepted"
          value={String(totals.accepted)}
          icon={CheckCircle2}
        />
        <MetricCard
          label="Declined"
          value={String(totals.declined)}
          icon={XCircle}
        />
      </div>

      <SavedViewTabs
        entityType={ENTITY_TYPE}
        saveInitialState={{
          filter: effectiveFilter,
          columns: active.columns,
          sort: active.sort,
        }}
      />

      {filterDriving ? (
        <FilterToolbar
          effectiveFilter={effectiveFilter}
          isOverride={filterIsOverride}
          onApplyFilter={setFilterInUrl}
          q={q}
          onQChange={(next) => updateParam("q", next)}
          activeView={active.view}
        />
      ) : (
        <ChipToolbar
          statusRaw={statusRaw}
          onStatusChange={(s) => updateParam("status", s)}
          q={q}
          onQChange={(next) => updateParam("q", next)}
          onApplyFilter={setFilterInUrl}
          activeView={active.view}
        />
      )}

      <div className="mt-4">
        {submissionsQuery.isLoading ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : submissionsQuery.isError ? (
          <EmptyState
            icon={FileText}
            title="Couldn't load submissions"
            description={
              submissionsQuery.error?.message ?? "Try refreshing."
            }
          />
        ) : filteredRows.length > 0 ? (
          <SubmissionsTable
            rows={filteredRows}
            selectedId={selectedId}
            onSelect={(id) => updateParam("submission", id)}
            visibleColumns={activeVisible.length > 0 ? activeVisible : undefined}
          />
        ) : (
          <EmptyState
            icon={q || statusRaw || filterDriving ? Search : FileText}
            title={
              q || statusRaw || filterDriving
                ? "No submissions match this view"
                : "No submissions yet"
            }
            description={
              q || statusRaw || filterDriving
                ? "Try clearing the search box, status chip, or filters."
                : "When partners or anonymous visitors submit a form, the entries land here for review."
            }
          />
        )}
      </div>

      <SubmissionDrawer
        submissionId={selectedId}
        onClose={() => updateParam("submission", null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chip-driven toolbar (no saved view, no URL filter).
// ---------------------------------------------------------------------------

function ChipToolbar({
  statusRaw,
  onStatusChange,
  q,
  onQChange,
  onApplyFilter,
  activeView,
}: {
  statusRaw: string | null;
  onStatusChange: (next: string) => void;
  q: string;
  onQChange: (next: string) => void;
  onApplyFilter: (tree: FilterTree | null) => void;
  activeView: ReturnType<typeof useActiveView>["view"];
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap items-center gap-1 rounded-md border bg-card p-0.5">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onStatusChange(opt.value)}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              (statusRaw ?? "all") === opt.value
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <AddFilterMenu
        entityType={ENTITY_TYPE}
        value={null}
        onApply={onApplyFilter}
      />

      <ActiveViewColumnsButton
        entityType={ENTITY_TYPE}
        activeView={activeView}
      />

      <div className="relative ml-auto min-w-[220px] max-w-md flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="Search by form or partner"
          className="pl-8"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilterTree-driven toolbar (saved view active OR URL filter set).
// ---------------------------------------------------------------------------

function FilterToolbar({
  effectiveFilter,
  isOverride,
  onApplyFilter,
  q,
  onQChange,
  activeView,
}: {
  effectiveFilter: FilterTree | null;
  isOverride: boolean;
  onApplyFilter: (tree: FilterTree | null) => void;
  q: string;
  onQChange: (next: string) => void;
  activeView: ReturnType<typeof useActiveView>["view"];
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <AddFilterMenu
        entityType={ENTITY_TYPE}
        value={effectiveFilter}
        onApply={onApplyFilter}
        isOverride={isOverride}
      />

      <ActiveViewColumnsButton
        entityType={ENTITY_TYPE}
        activeView={activeView}
      />

      <div className="relative ml-auto min-w-[220px] max-w-md flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="Search by form or partner"
          className="pl-8"
        />
      </div>
    </div>
  );
}
